// src/pages/PixManual.js
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Copy, CheckCircle, MessageCircle } from "lucide-react";
import styles from "../styles/pix-manual.module.css";

function formatBRL(v) {
  if (v == null || isNaN(v)) return "R$ 0,00";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * Props esperadas:
 *  - valor: number (total com taxa)
 *  - senhas: array de objetos { numero?: string|number }  (opcional)
 *  - nomeVaqueiro: string (opcional)
 */
export default function PixManual({ valor = 0, senhas = [], nomeVaqueiro = "" }) {
  const [cfg, setCfg] = useState({
    chave: "",
    favorecido: "",
    banco: "",
    whatsappDestino: "", // e.g. "5583991740119"
    enabled: true,
  });

  const [copiado, setCopiado] = useState(false);

  // Busca a config do Pix Manual no admin
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/gateways`);
        const pm = data?.credentials?.pix_manual || {};
        if (!mounted) return;
        setCfg({
          chave: pm.chave || "",
          favorecido: pm.favorecido || "",
          banco: pm.banco || "",
          whatsappDestino: pm.whatsappDestino || "",
          enabled: pm.enabled !== false,
        });
      } catch (e) {
        console.warn("Falha ao carregar Pix Manual de /gateways. Você pode configurar no Admin > Gateways.");
        // Fallback opcional (vazio). Deixe assim para obrigar a preencher no admin.
        setCfg({ chave: "", favorecido: "", banco: "", whatsappDestino: "", enabled: true });
      }
    })();
    return () => { mounted = false; };
  }, []);

  const numerosDasSenhas = useMemo(() => {
    // aceita array de números ou de objetos {numero}
    return (senhas || []).map((s) => (typeof s === "object" ? s?.numero : s)).filter(Boolean);
  }, [senhas]);

  const textoWhatsapp = useMemo(() => {
    const partes = [];
    if (numerosDasSenhas.length) partes.push(`Senhas: ${numerosDasSenhas.join(", ")}`);
    if (nomeVaqueiro) partes.push(`Comprador: ${nomeVaqueiro}`);
    partes.push(`Valor: ${formatBRL(Number(valor))}`);
    partes.push("Envio o comprovante em anexo. Obrigado!");
    return `Olá! Acabei de realizar um pagamento via Pix Manual.\n${partes.join("\n")}`;
  }, [numerosDasSenhas, nomeVaqueiro, valor]);

  const linkWhatsapp = useMemo(() => {
    const destino = (cfg.whatsappDestino || "").replace(/\D/g, "");
    if (!destino) return null;
    return `https://wa.me/${destino}?text=${encodeURIComponent(textoWhatsapp)}`;
  }, [cfg.whatsappDestino, textoWhatsapp]);

  const copiarChave = async () => {
    try {
      await navigator.clipboard.writeText(cfg.chave || "");
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      alert("Não foi possível copiar. Copie manualmente a chave abaixo.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h3 className={styles.titulo}>Pagamento via Pix Manual</h3>

        <div className={styles.valorBox}>
          <span className={styles.valorLabel}>Valor a pagar</span>
          <span className={styles.valor}>{formatBRL(Number(valor))}</span>
        </div>

        {!!numerosDasSenhas.length && (
          <div className={styles.senhasBox}>
            <span className={styles.senhasLabel}>Senhas:</span>
            <span className={styles.senhasLista}>{numerosDasSenhas.join(", ")}</span>
          </div>
        )}

        <div className={styles.infoBox}>
          <div className={styles.linha}>
            <span className={styles.label}>Chave Pix</span>
            <div className={styles.chaveRow}>
              <span className={styles.chave}>{cfg.chave || "—"}</span>
              <button onClick={copiarChave} className={styles.botaoCopiar} disabled={!cfg.chave}>
                {copiado ? <CheckCircle size={16} /> : <Copy size={16} />}
                {copiado ? "Copiado!" : "Copiar"}
              </button>
            </div>
          </div>

          <div className={styles.linha}>
            <span className={styles.label}>Favorecido</span>
            <span className={styles.valorTxt}>{cfg.favorecido || "—"}</span>
          </div>

          <div className={styles.linha}>
            <span className={styles.label}>Banco</span>
            <span className={styles.valorTxt}>{cfg.banco || "—"}</span>
          </div>
        </div>

        <div className={styles.alerta}>
          <p>
            Faça um Pix usando a <strong>Chave Pix</strong> acima. Após o pagamento, envie o comprovante
            para agilizar a confirmação.
          </p>
        </div>

        <div className={styles.acoes}>
          {linkWhatsapp ? (
            <a href={linkWhatsapp} target="_blank" rel="noreferrer" className={styles.btnWhats}>
              <MessageCircle size={18} /> Enviar comprovante pelo WhatsApp
            </a>
          ) : (
            <button className={styles.btnWhats} disabled>
              Configure o WhatsApp no Admin &gt; Gateways
            </button>
          )}
        </div>

        {!cfg.enabled && (
          <p className={styles.desabilitado}>
            O Pix Manual está desabilitado no Admin &gt; Gateways.
          </p>
        )}
      </div>
    </div>
  );
}
